import { useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  Condition,
  OrderByModel,
  Pagination,
} from "@tsmono/inspect-common/query";
import {
  useAsyncDataFromQuery,
  useDebouncedCallback,
} from "@tsmono/react/hooks";
import type { AsyncData } from "@tsmono/util";

import type { LogListingRow } from "../../../log_data";
import {
  databaseLogsListingKey,
  listingKeyUniverse,
  readLogsListing,
  readLogsListingMatches,
} from "../../../log_data";

import { applyListingQuery } from "./applyListingQuery";
import { createListingPlan } from "./planner";
import type {
  FilterTypeAccessor,
  LogsListingResult,
  ValueAccessor,
  ValueComparator,
} from "./types";

/** TanStack `SortingState` → API `OrderBy[]`. Mirrors scout's helper. */
export const sortingStateToOrderBy = (sorting: SortingState): OrderByModel[] =>
  sorting.map((s) => ({ column: s.id, direction: s.desc ? "DESC" : "ASC" }));

interface UseLogsListingParams<TRow> {
  /** Shaped rows (the panel owns shaping; sourced from the logs-content cache). */
  rows: TRow[];
  filter?: Condition;
  orderBy?: OrderByModel[];
  pagination?: Pagination;
  getValue: ValueAccessor<TRow>;
  getComparator: (columnId: string) => ValueComparator | undefined;
  getFilterType?: FilterTypeAccessor;
}

/**
 * The generic in-memory listing query: filter + sort + paginate over the
 * rows. Mirrors scout's `getTranscripts(filter, orderBy, pagination)` tail
 * and response shape. Retained for samples listings, which don't have a
 * database-backed path yet — the log list uses
 * {@link useDatabaseLogsListingQuery}.
 */
export function useLogsListingQuery<TRow>({
  rows,
  filter,
  orderBy,
  pagination,
  getValue,
  getComparator,
  getFilterType,
}: UseLogsListingParams<TRow>): LogsListingResult<TRow> {
  return useMemo(
    () =>
      applyListingQuery(rows, {
        filter,
        orderBy,
        pagination,
        getValue,
        getComparator,
        getFilterType,
      }),
    [rows, filter, orderBy, pagination, getValue, getComparator, getFilterType]
  );
}

/** The listing source a view queries — shared by the row query, the find
 *  band's match query, and (eventually) the offset lookup, so they can
 *  never disagree about the row universe. */
export interface LogsListingDescriptor<TRow> {
  /** The synced directory whose rows are the source (see `readLogsListing`
   *  for where they're read from). */
  logDir: string;
  /** Row-universe scan prefix (folder mode lists a subdirectory). */
  prefix: string;
  /** Cache identity of the row universe: everything `toRow` reads beyond
   *  the record itself (view mode, directory, display toggles).
   *  `undefined` while the scope is still hydrating — disables queries. */
  universe: string | undefined;
  /** Shape a source record into the view's row, or `undefined` when the
   *  view has no row for it (row-universe membership). */
  toRow: (log: LogListingRow) => TRow | undefined;
}

interface UseDatabaseLogsListingParams<TRow> {
  filter?: Condition;
  orderBy?: OrderByModel[];
  pagination?: Pagination;
  getValue: ValueAccessor<TRow>;
  getComparator: (columnId: string) => ValueComparator | undefined;
  getFilterType?: FilterTypeAccessor;
  /** Cache identity of the accessors above (see `useLogListColumns`) — the
   *  score-column schema lands asynchronously and changes what the plan
   *  computes, so it must key the query alongside filter/orderBy. */
  accessorsKey: string;
  listing: LogsListingDescriptor<TRow>;
}

/**
 * The log listing query: rows are read from the listing source (IndexedDB
 * in dir mode — see `readLogsListing`) and shaped per view inside the
 * queryFn, so the full row list never has to live in memory for the grid's
 * sake. Results are asynchronous by design: the first read shows whatever
 * has replicated so far, and the write path's throttled invalidation
 * streams further rows in as they land. `loading` covers hydration and the
 * universe's first read; within one universe a re-filter/sort reports the
 * previous result as `data` (no loading flash) until the new read lands.
 */
export function useDatabaseLogsListingQuery<TRow>({
  filter,
  orderBy,
  pagination,
  getValue,
  getComparator,
  getFilterType,
  accessorsKey,
  listing,
}: UseDatabaseLogsListingParams<TRow>): AsyncData<LogsListingResult<TRow>> {
  const { logDir, prefix, universe, toRow } = listing;
  return useAsyncDataFromQuery({
    queryKey: databaseLogsListingKey(
      universe,
      accessorsKey,
      filter,
      orderBy,
      pagination
    ),
    queryFn: (): Promise<LogsListingResult<TRow>> =>
      readLogsListing(
        logDir,
        prefix,
        toRow,
        createListingPlan({
          filter,
          orderBy,
          pagination,
          getValue,
          getComparator,
          getFilterType,
        })
      ),
    enabled: universe !== undefined,
    // Keep showing the previous result across re-filters/sorts — and schema
    // arrivals — within one universe (same row set, possibly re-evaluated);
    // a different universe's rows must not leak in.
    placeholderData: (previousData, previousQuery) =>
      universe !== undefined &&
      previousQuery !== undefined &&
      listingKeyUniverse(previousQuery.queryKey) === universe
        ? previousData
        : undefined,
    staleTime: 0,
    // Transitional (pre-pagination): each key holds the full shaped row
    // list, so the default 5-minute gc would keep a full copy per recent
    // (schema, filter, orderBy) combination. Drop unobserved copies fast.
    gcTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

interface UseLogsListingMatchesParams<TRow> {
  /** The same query inputs the row query ran under (pass them through from
   *  `useLogListData` rather than re-deriving — the match membership must
   *  never disagree with the rendered rows). */
  filter?: Condition;
  orderBy?: OrderByModel[];
  getValue: ValueAccessor<TRow>;
  getComparator: (columnId: string) => ValueComparator | undefined;
  getFilterType?: FilterTypeAccessor;
  accessorsKey: string;
  listing: LogsListingDescriptor<TRow>;
  /** The live find term. The query runs under a debounced copy: every
   *  distinct term is a fresh scan of the listing source, so keystrokes
   *  coalesce here while the input (and cheap overlay matching) stay live. */
  term: string;
  /** Whether the find band is open — the query never runs while closed. */
  enabled: boolean;
  getRowId: (row: TRow) => string;
  /** A row's searchable text, already lowercased (see `rowSearchText`). */
  rowText: (row: TRow) => string;
  /** Cache identity of `rowText` — the searchable (visible) column ids. */
  searchKey: readonly string[];
}

export interface LogsListingMatches {
  /** Ids of the file rows matching the debounced term — membership only;
   *  display order comes from the rendered rows. */
  ids: string[] | undefined;
  /** `ids` is a real result for the live term under the current universe —
   *  debounce flushed, not pending, not another key's placeholder, not an
   *  error. Only then may the UI claim "no results". */
  settled: boolean;
  /** Cancel the pending debounce and clear the match term (band closed). */
  reset: () => void;
}

/**
 * The find band's data-level match query, beside the row query so the two
 * share key shape and universe semantics: the key is the row query's key
 * (same universe slot, so `listingKeyUniverse` and the root invalidation
 * cover both) extended with the find-only inputs, and the placeholder keeps
 * previous matches only within one universe — folder-mode row ids are
 * basenames, so another directory's ids could otherwise mark unrelated
 * same-named rows as matches while a scope change's refetch is in flight.
 */
export function useLogsListingMatches<TRow>({
  filter,
  orderBy,
  getValue,
  getComparator,
  getFilterType,
  accessorsKey,
  listing,
  term,
  enabled,
  getRowId,
  rowText,
  searchKey,
}: UseLogsListingMatchesParams<TRow>): LogsListingMatches {
  const [matchTerm, setMatchTerm] = useState("");
  // Same 100ms as the shared FindBand's debounce. The debounced callback
  // always runs the latest closure, so the flush reads the current term.
  const syncMatchTerm = useDebouncedCallback(() => setMatchTerm(term), 100);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    syncMatchTerm();
  }, [term, syncMatchTerm]);
  const reset = useCallback(() => {
    syncMatchTerm.cancel();
    setMatchTerm("");
  }, [syncMatchTerm]);

  const { logDir, prefix, universe, toRow } = listing;
  const query = useQuery({
    queryKey: [
      ...databaseLogsListingKey(universe, accessorsKey, filter, orderBy),
      "find",
      matchTerm,
      searchKey,
    ],
    queryFn: (): Promise<string[]> =>
      readLogsListingMatches(
        logDir,
        prefix,
        toRow,
        createListingPlan({
          filter,
          orderBy,
          getValue,
          getComparator,
          getFilterType,
        }),
        { term: matchTerm, getRowId, rowText }
      ),
    enabled: enabled && matchTerm !== "" && universe !== undefined,
    // Keep the previous matches while a keystroke's refetch is in flight —
    // within one universe only (see the docstring above).
    placeholderData: (previousData: string[] | undefined, previousQuery) =>
      universe !== undefined &&
      previousQuery !== undefined &&
      listingKeyUniverse(previousQuery.queryKey) === universe
        ? previousData
        : undefined,
    staleTime: 0,
    // Transitional (pre-pagination): every distinct term parks a full id
    // list per key; drop unobserved ones fast, like the row query.
    gcTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    ids: query.data,
    // `isPending` alone can't gate "no results": a key change served from
    // the placeholder reads as success while the new term's scan is still
    // in flight, and an errored query reads as not-pending with no data.
    settled:
      term === matchTerm &&
      !query.isPending &&
      !query.isPlaceholderData &&
      !query.isError,
    reset,
  };
}
